import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const PRODUCT_FORM_SOURCE = 'product-form-component';
const CROSS_SALE_SOURCE = 'cross-sale-popup';
const PRODUCT_LIMIT = 5;

/**
 * @returns {HTMLElement | null}
 */
function getPopup() {
  return document.getElementById('cross-sale-popup');
}

/**
 * @returns {HTMLElement | null}
 */
function getPopupBody() {
  return getPopup()?.querySelector('.cross-sale-popup__body') ?? null;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;');
}

/**
 * @returns {boolean}
 */
function showPopupDialog() {
  const popup = getPopup();
  const dialog = popup?.querySelector('dialog');
  if (!(dialog instanceof HTMLDialogElement)) return false;

  if (typeof popup.showDialog === 'function') {
    try {
      popup.showDialog();
    } catch (error) {
      console.error('Cross-sale popup error:', error);
      if (!dialog.open) dialog.showModal();
    }
  } else if (!dialog.open) {
    dialog.showModal();
  }

  return true;
}

function closePopup() {
  const popup = getPopup();
  const dialog = popup?.querySelector('dialog');

  if (typeof popup?.closeDialog === 'function' && dialog instanceof HTMLDialogElement && dialog.open) {
    return popup.closeDialog();
  }

  if (dialog instanceof HTMLDialogElement && dialog.open) {
    dialog.close();
  }

  return Promise.resolve();
}

/**
 * @param {Event} event
 * @param {{ productId?: string }} [data]
 * @returns {string}
 */
function getProductId(event, data) {
  const target = event.target instanceof HTMLElement ? event.target : null;

  return (
    data?.productId ||
    target?.dataset?.productId ||
    target?.closest?.('[data-product-id]')?.dataset.productId ||
    document.querySelector('#MainContent product-form-component[data-product-id]')?.dataset.productId ||
    ''
  );
}

/**
 * @param {number | string} cents
 * @returns {string}
 */
function formatPrice(cents) {
  const popup = getPopup();
  const currency = popup?.dataset.currency || 'USD';
  const amount = (Number(cents) || 0) / (currency === 'JPY' ? 1 : 100);

  if (currency === 'MXN') {
    return `$${Math.round(amount)}MXN`;
  }

  const format = popup?.dataset.moneyFormat || '${{amount}}';
  return formatMoney(Number(cents) || 0, format, currency);
}

/**
 * @param {string} productId
 * @param {'complementary' | 'related'} intent
 * @returns {Promise<Node | null>}
 */
async function fetchJsonRecommendations(productId, intent) {
  const popup = getPopup();
  if (!popup) return null;

  const recommendationsUrl = popup.dataset.recommendationsUrl;
  const limit = popup.dataset.limit || String(PRODUCT_LIMIT);
  if (!recommendationsUrl) return null;

  const base = recommendationsUrl.endsWith('.json') ? recommendationsUrl : `${recommendationsUrl}.json`;
  const jsonUrl = `${base}?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${intent}`;
  const response = await fetch(jsonUrl);
  if (!response.ok) return null;

  const data = await response.json();
  const products = Array.isArray(data?.products) ? data.products.slice(0, PRODUCT_LIMIT) : [];
  if (!products.length) return null;

  const wrapper = document.createElement('div');
  wrapper.className = 'cross-sale-popup__json';
  wrapper.innerHTML = `<ul class="cross-sale-popup__grid"></ul>`;

  const list = wrapper.querySelector('ul');
  if (!list) return wrapper;

  for (const product of products) {
    const image =
      (typeof product.featured_image === 'string' && product.featured_image) ||
      product.featured_image?.url ||
      product.featured_image?.src ||
      product.images?.[0]?.src ||
      '';
    const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
    const url = product.url || `/products/${product.handle}`;
    const price = variant?.price ?? product.price;
    const item = document.createElement('li');
    item.className = 'cross-sale-popup__row';
    item.dataset.productId = String(product.id);
    item.innerHTML = `
      <a class="cross-sale-popup__image-link" href="${escapeHtml(url)}">
        ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title || '')}" width="192" height="211">` : ''}
      </a>
      <div class="cross-sale-popup__details">
        <div class="cross-sale-popup__field cross-sale-popup__field--name">
          <span class="cross-sale-popup__label">Nombre</span>
          <a class="cross-sale-popup__card-title" href="${escapeHtml(url)}">${escapeHtml(product.title || '')}</a>
        </div>
        <div class="cross-sale-popup__field">
          <span class="cross-sale-popup__label">Precio</span>
          <span class="cross-sale-popup__price">${escapeHtml(formatPrice(price))}</span>
        </div>
        <div class="cross-sale-popup__field">
          <span class="cross-sale-popup__label">Cant.</span>
          <div class="cross-sale-popup__qty">
            <button class="cross-sale-popup__qty-btn" type="button" data-qty-step="-1" aria-label="Decrease quantity">−</button>
            <input class="cross-sale-popup__qty-input" type="number" name="quantity" value="1" min="1" aria-label="Quantity">
            <button class="cross-sale-popup__qty-btn" type="button" data-qty-step="1" aria-label="Increase quantity">+</button>
          </div>
        </div>
        ${
          variant?.id
            ? `<div class="cross-sale-popup__field cross-sale-popup__field--action">
                <button type="button" class="cross-sale-popup__add" data-variant-id="${escapeHtml(variant.id)}" data-product-id="${escapeHtml(product.id)}">AÑADIR</button>
              </div>`
            : ''
        }
      </div>
    `;
    list.appendChild(item);
  }

  return wrapper;
}

/**
 * @param {ParentNode} root
 * @param {number} [max]
 */
function limitProducts(root, max = PRODUCT_LIMIT) {
  const items = [...root.querySelectorAll('.cross-sale-popup__row, .product-grid__item:not(.product-recommendations__skeleton-item)')];
  items.forEach((item, index) => {
    if (index >= max) item.remove();
  });
}

/**
 * @param {string} productId
 * @returns {Promise<Node | null>}
 */
async function loadRecommendations(productId) {
  for (const intent of /** @type {const} */ (['complementary', 'related'])) {
    try {
      const jsonNode = await fetchJsonRecommendations(productId, intent);
      if (jsonNode) {
        limitProducts(jsonNode);
        return jsonNode;
      }
    } catch (error) {
      console.error('Cross-sale json fetch error:', error);
    }
  }

  return null;
}

/**
 * @param {string} productId
 * @returns {Promise<boolean>}
 */
async function openPopup(productId) {
  const body = getPopupBody();
  if (!body) return false;

  body.innerHTML = '<p class="cross-sale-popup__loading">Loading...</p>';
  if (!showPopupDialog()) return false;
  if (!productId) return false;

  const content = await loadRecommendations(productId);
  if (!content) return false;

  body.replaceChildren(content);
  if (!showPopupDialog()) return false;
  return true;
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.showDialog === 'function') {
    drawer.showDialog();
    return;
  }

  if (Theme?.routes?.cart_url) {
    window.location.href = Theme.routes.cart_url;
  }
}

/**
 * @param {MouseEvent} event
 */
async function handleFooterClick(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-cross-sale-action]') : null;
  if (!button) return;

  const action = button.getAttribute('data-cross-sale-action');
  if (action === 'continue') {
    await closePopup();
    return;
  }

  if (action === 'cart') {
    await closePopup();
    openCartDrawer();
  }
}

/**
 * @param {Event} event
 */
function handleCartAdd(event) {
  const detail = /** @type {{ data?: { didError?: boolean, source?: string, productId?: string, skipDrawer?: boolean } }} */ (
    event
  ).detail;
  const data = detail?.data;
  if (!data || data.didError) return;

  const source = data.source || (event.target instanceof HTMLElement ? event.target.localName : '');
  if (source === 'cart-items-component') return;
  if (source === CROSS_SALE_SOURCE) {
    data.skipDrawer = true;
    return;
  }
  if (source !== PRODUCT_FORM_SOURCE && event.target?.localName !== PRODUCT_FORM_SOURCE) return;

  data.skipDrawer = true;
  const productId = getProductId(event, data);

  openPopup(productId)
    .then((shown) => {
      if (shown) return;
      closePopup();
      data.skipDrawer = false;
      openCartDrawer();
    })
    .catch((error) => {
      console.error('Cross-sale popup error:', error);
      closePopup();
      data.skipDrawer = false;
      openCartDrawer();
    });
}

/**
 * @param {MouseEvent} event
 */
async function handleJsonAdd(event) {
  const button = event.target instanceof Element ? event.target.closest('.cross-sale-popup__add') : null;
  if (!button) return;

  const variantId = button.getAttribute('data-variant-id');
  const productId = button.getAttribute('data-product-id');
  const row = button.closest('.cross-sale-popup__row');
  const qtyInput = row?.querySelector('.cross-sale-popup__qty-input, input[ref="quantityInput"]');
  const quantity = Math.max(1, Number(qtyInput?.value) || 1);
  if (!variantId || button.hasAttribute('disabled')) return;

  button.setAttribute('disabled', '');

  try {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: sectionIds.join(','),
      }),
    });
    const payload = await response.json();
    if (payload.status) return;

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartAddEvent(cart, variantId, {
        source: CROSS_SALE_SOURCE,
        productId: productId || undefined,
        sections: payload.sections,
      })
    );
  } catch (error) {
    console.error('Cross-sale add error:', error);
  } finally {
    button.removeAttribute('disabled');
  }
}

/**
 * @param {MouseEvent} event
 */
function handleQuantityClick(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-qty-step]') : null;
  if (!button || !button.closest('.cross-sale-popup__qty')) return;

  const input = button.parentElement?.querySelector('.cross-sale-popup__qty-input');
  if (!(input instanceof HTMLInputElement)) return;

  const step = Number(button.getAttribute('data-qty-step')) || 0;
  const min = Number(input.min) || 1;
  input.value = String(Math.max(min, (Number(input.value) || min) + step));
}

function init() {
  document.addEventListener(CartAddEvent.eventName, handleCartAdd, true);
  const popup = getPopup();
  popup?.addEventListener('click', handleJsonAdd);
  popup?.addEventListener('click', handleQuantityClick);
  popup?.addEventListener('click', handleFooterClick);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
